import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import SEO from '../../components/SEO';

export default function SustainabilityPage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://trigreensoul.com';

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Sustainability & Certifications",
    "description": "Our dedication to environmental stewardship goes beyond organic certification—it's embedded in every aspect of our operations.",
    "url": `${siteUrl}/sustainability`,
    "mainEntity": {
      "@type": "Organization",
      "name": "TRIGREENSOUL",
      "hasCredential": [
        {
          "@type": "EducationalOccupationalCredential",
          "credentialCategory": "certification",
          "name": "EU Organic Certification",
          "description": "All products meet strict European Union organic farming regulations (EC 834/2007 and EC 889/2008)"
        }
      ],
      "knowsAbout": [
        "Organic Farming",
        "Circular Economy",
        "Resource Conservation",
        "Social Responsibility",
        "Sustainable Agriculture"
      ],
      "subjectOf": {
        "@type": "CreativeWork",
        "about": "Sustainability Commitment",
        "text": "Four core principles guide our environmental responsibility: Organic Farming, Circular Economy, Resource Conservation, and Social Responsibility"
      }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Sustainability & Certifications - TRIGREENSOUL | EU Organic Certified | Barcelona"
        description="Our dedication to environmental stewardship goes beyond organic certification. Learn about our sustainability pillars: organic farming, circular economy, resource conservation, and social responsibility. EU Organic certified."
        keywords="organic certification, EU organic standards, sustainable agriculture, organic farming practices, environmental sustainability, circular economy, Barcelona organic certification"
        canonical={`${siteUrl}/sustainability`}
        schema={schema}
      />

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://public.readdy.ai/ai/img_res/ae0a5222-1684-471a-9ad4-134e3508a5e0.png" alt="TRIGREENSOUL" className="h-12 w-auto" />
            <span className="text-xl font-semibold text-gray-900">TRIGREENSOUL</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/about" className="text-sm font-medium text-gray-700 hover:text-emerald-600 transition-colors">About</Link>
            <Link to="/products" className="text-sm font-medium text-gray-700 hover:text-emerald-600 transition-colors">Products</Link>
            <Link to="/services" className="text-sm font-medium text-gray-700 hover:text-emerald-600 transition-colors">Services</Link>
            <Link to="/sustainability" className="text-sm font-medium text-emerald-700">Sustainability</Link>
            <Link to="/contact" className="px-6 py-2 rounded-full border-2 border-emerald-700 text-emerald-700 hover:bg-emerald-700 hover:text-white transition-all whitespace-nowrap cursor-pointer">Partner With Us</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-emerald-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl">
            <span className="text-sm text-emerald-700 uppercase tracking-wider font-semibold">Sustainability & Certifications</span>
            <h1 className="text-6xl md:text-7xl font-bold text-gray-900 mt-4 mb-8 leading-tight">
              Committed to a<br />Greener Future
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Our dedication to environmental stewardship goes beyond organic certification—it's embedded in every aspect of our operations, from farm partnerships to final delivery.
            </p>
          </div>
        </div>
      </section>

      {/* Sustainability Pillars */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">Our Sustainability Pillars</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Four core principles guide our environmental responsibility and sustainable business practices
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-emerald-50 rounded-2xl p-10 hover:shadow-xl transition-all">
              <div className="w-16 h-16 flex items-center justify-center mb-6 bg-emerald-700 rounded-xl">
                <i className="ri-leaf-line text-4xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Organic Farming</h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                We exclusively partner with certified organic farms that prioritize soil health, biodiversity, and natural pest management. No synthetic pesticides, herbicides, or GMOs—ever.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-700 flex-shrink-0 mt-1"></i>
                  <span>100% certified organic produce</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-700 flex-shrink-0 mt-1"></i>
                  <span>Support for regenerative agriculture practices</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-700 flex-shrink-0 mt-1"></i>
                  <span>Promotion of crop rotation and biodiversity</span>
                </li>
              </ul>
            </div>
            <div className="bg-amber-50 rounded-2xl p-10 hover:shadow-xl transition-all">
              <div className="w-16 h-16 flex items-center justify-center mb-6 bg-emerald-700 rounded-xl">
                <i className="ri-recycle-line text-4xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Circular Economy</h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                We minimize waste through innovative packaging solutions, composting programs, and partnerships that give agricultural byproducts new life.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-700 flex-shrink-0 mt-1"></i>
                  <span>Recyclable and compostable packaging materials</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-700 flex-shrink-0 mt-1"></i>
                  <span>Zero-waste initiatives across operations</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-700 flex-shrink-0 mt-1"></i>
                  <span>Organic waste composting programs</span>
                </li>
              </ul>
            </div>
            <div className="bg-stone-100 rounded-2xl p-10 hover:shadow-xl transition-all">
              <div className="w-16 h-16 flex items-center justify-center mb-6 bg-emerald-700 rounded-xl">
                <i className="ri-water-flash-line text-4xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Resource Conservation</h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                Efficient use of water, energy, and natural resources is fundamental to our operations and the farms we work with.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-700 flex-shrink-0 mt-1"></i>
                  <span>Water-efficient irrigation systems</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-700 flex-shrink-0 mt-1"></i>
                  <span>Renewable energy in facilities and transport</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-700 flex-shrink-0 mt-1"></i>
                  <span>Optimized logistics to reduce fuel consumption</span>
                </li>
              </ul>
            </div>
            <div className="bg-emerald-50 rounded-2xl p-10 hover:shadow-xl transition-all">
              <div className="w-16 h-16 flex items-center justify-center mb-6 bg-emerald-700 rounded-xl">
                <i className="ri-team-line text-4xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Social Responsibility</h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                Fair treatment of farmers, workers, and communities is essential to building a truly sustainable food system.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-700 flex-shrink-0 mt-1"></i>
                  <span>Fair pricing for organic farmers</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-700 flex-shrink-0 mt-1"></i>
                  <span>Safe working conditions throughout supply chain</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-700 flex-shrink-0 mt-1"></i>
                  <span>Community development programs</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">Certifications & Standards</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our commitment to quality and sustainability is verified by leading international certification bodies
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-10 text-center hover:shadow-xl transition-all">
              <div className="w-24 h-24 flex items-center justify-center mx-auto mb-6 bg-emerald-100 rounded-full">
                <i className="ri-shield-check-line text-5xl text-emerald-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">EU Organic Certification</h3>
              <p className="text-gray-600 leading-relaxed">
                All products meet strict European Union organic farming regulations (EC 834/2007 and EC 889/2008), ensuring authenticity and quality.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-10 text-center hover:shadow-xl transition-all">
              <div className="w-24 h-24 flex items-center justify-center mx-auto mb-6 bg-emerald-100 rounded-full">
                <i className="ri-global-line text-5xl text-emerald-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">International Standards</h3>
              <p className="text-gray-600 leading-relaxed">
                Compliance with IFOAM principles and various national organic standards for seamless international trade.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-10 text-center hover:shadow-xl transition-all">
              <div className="w-24 h-24 flex items-center justify-center mx-auto mb-6 bg-emerald-100 rounded-full">
                <i className="ri-file-list-3-line text-5xl text-emerald-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Traceability Systems</h3>
              <p className="text-gray-600 leading-relaxed">
                Complete supply chain documentation and tracking systems ensuring transparency from farm to final destination.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Environmental Impact */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-5xl font-bold text-gray-900 mb-8 leading-tight">
                Measuring Our Impact
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                We believe in transparency and accountability. That's why we continuously monitor and report on our environmental performance across key metrics.
              </p>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 bg-emerald-100 rounded-lg">
                    <i className="ri-arrow-down-line text-2xl text-emerald-700"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Carbon Footprint Reduction</h3>
                    <p className="text-gray-600">
                      Ongoing initiatives to minimize emissions through route optimization, electric vehicles, and renewable energy adoption.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 bg-emerald-100 rounded-lg">
                    <i className="ri-drop-line text-2xl text-emerald-700"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Water Conservation</h3>
                    <p className="text-gray-600">
                      Supporting farms that use drip irrigation and water-saving techniques, reducing agricultural water usage significantly.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 bg-emerald-100 rounded-lg">
                    <i className="ri-seedling-line text-2xl text-emerald-700"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Biodiversity Protection</h3>
                    <p className="text-gray-600">
                      Partnering with farms that maintain natural habitats, hedgerows, and pollinator-friendly environments.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative h-[600px] rounded-2xl overflow-hidden shadow-2xl">
              <img src="https://public.readdy.ai/ai/img_res/9049d04bddb428d52ce788e8e40d7b22.jpg" alt="Environmental impact" className="w-full h-full object-cover object-center" />
            </div>
          </div>
        </div>
      </section>

      {/* Future Goals */}
      <section className="py-24 bg-gradient-to-br from-emerald-900 to-emerald-800 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">Our 2030 Goals</h2>
            <p className="text-xl text-emerald-100 max-w-3xl mx-auto">
              Ambitious targets driving us toward a more sustainable future
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-6xl font-bold mb-4">100%</div>
              <h3 className="text-xl font-bold mb-2">Carbon Neutral</h3>
              <p className="text-emerald-100">
                Complete carbon neutrality across all operations and supply chain
              </p>
            </div>
            <div className="text-center">
              <div className="text-6xl font-bold mb-4">50%</div>
              <h3 className="text-xl font-bold mb-2">Waste Reduction</h3>
              <p className="text-emerald-100">
                Halving operational waste through circular economy practices
              </p>
            </div>
            <div className="text-center">
              <div className="text-6xl font-bold mb-4">500+</div>
              <h3 className="text-xl font-bold mb-2">Partner Farms</h3>
              <p className="text-emerald-100">
                Expanding our network of certified organic farming partners
              </p>
            </div>
            <div className="text-center">
              <div className="text-6xl font-bold mb-4">100%</div>
              <h3 className="text-xl font-bold mb-2">Renewable Energy</h3>
              <p className="text-emerald-100">
                Powering all facilities with clean, renewable energy sources
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-bold text-gray-900 mb-6">Join Our Sustainable Journey</h2>
          <p className="text-xl text-gray-600 mb-10 leading-relaxed">
            Partner with us to build a more sustainable food system that benefits people, planet, and prosperity
          </p>
          <Link to="/contact" className="inline-block px-10 py-4 bg-emerald-700 text-white text-lg font-semibold rounded-full hover:bg-emerald-800 transition-all hover:scale-105 cursor-pointer whitespace-nowrap">
            Become a Partner →
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white pt-20 pb-12 mx-8 mt-16 rounded-t-3xl">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div>
              <h3 className="text-4xl font-light mb-4">Stay Connected</h3>
              <p className="text-gray-400 mb-8">Subscribe to organic industry insights</p>
            </div>
            <div>
              <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-6">Quick Links</h4>
              <div className="space-y-4">
                <Link to="/about" className="block text-white hover:text-emerald-400 transition-colors cursor-pointer">About Us</Link>
                <Link to="/products" className="block text-white hover:text-emerald-400 transition-colors cursor-pointer">Products</Link>
                <Link to="/sustainability" className="block text-white hover:text-emerald-400 transition-colors cursor-pointer">Certifications</Link>
                <Link to="/contact" className="block text-white hover:text-emerald-400 transition-colors cursor-pointer">Contact</Link>
              </div>
            </div>
            <div>
              <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-6">Contact</h4>
              <p className="text-white mb-8">info@trigreensoul.com</p>
              <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-4">Headquarters</h4>
              <p className="text-white text-sm leading-relaxed">
                Barcelona, Spain<br />
                European Union
              </p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">© 2025 TRIGREENSOUL. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">Privacy Policy</a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">Terms of Service</a>
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-emerald-400 transition-colors cursor-pointer text-sm">Web Design</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
